import { Link, useLocation, useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  QrCode,
  AlertTriangle,
  LogOut,
  GraduationCap,
  FileText,
  UserCheck,
  Megaphone,
  ChevronDown,
  ChevronRight,
  Car,
  FolderTree,
  Upload,
  UserCog,
  Activity,
  MessageSquare,
  ShieldCheck,
  XCircle
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { base44 } from "@/api/base44Client";
import RoleSwitcher from "@/components/RoleSwitcher";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useEffect } from "react";

function LayoutContent({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  const [openMenus, setOpenMenus] = useState({
    eaSystem: true,
    userTypes: true,
    settings: false
  });
  const queryClient = useQueryClient();

  const { data: user, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  // Check if user has authorized email domain - ADMINS ALWAYS BYPASS THIS CHECK
  const isAuthorizedDomain = useMemo(() => {
    if (!user?.email) return false;
    
    // ✅ ADMIN BYPASS: Admins can use any email domain for testing
    if (user.role === 'admin') return true;
    
    const email = user.email.toLowerCase();
    // 🚨 TEMPORARY: Gmail access enabled for testing - REMOVE AFTER TESTING
    return email.endsWith('@iltexasstudent.org') || email.endsWith('@iltexas.org') || email.endsWith('@gmail.com');
  }, [user?.email, user?.role]); // user?.role added to dependencies

  // Fetch domain mapping settings
  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
    enabled: !!user,
  });

  const domainSettings = settings.find(s => s.setting_key === 'domain_mapping') || {};

  // NEW: Fetch support tickets to check for new tickets
  const { data: supportTickets = [] } = useQuery({
    queryKey: ['supportTickets'],
    queryFn: () => base44.entities.SupportTicket.list(),
    enabled: !!user && user?.role === 'admin',
  });

  const newTicketsCount = supportTickets.filter(t => t.status === 'new').length;

  // Auto-assign user type based on email domain
  const autoAssignUserTypeMutation = useMutation({
    mutationFn: async ({ userId, userType }) => {
      return base44.entities.User.update(userId, { user_type: userType });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['currentUser']);
    },
  });

  useEffect(() => {
    if (!user || !domainSettings.domain_auto_assignment_enabled) return;
    
    // Skip if user already has a user_type or is admin
    if (user.user_type || user.role === 'admin') return;

    const email = user.email?.toLowerCase();
    if (!email) return;

    // Check student domain
    if (domainSettings.student_email_domain && email.endsWith(domainSettings.student_email_domain.toLowerCase())) {
      console.log('Auto-assigning student role based on email domain');
      autoAssignUserTypeMutation.mutate({ userId: user.id, userType: 'student' });
      return;
    }

    // Check teacher domain
    if (domainSettings.teacher_email_domain && email.endsWith(domainSettings.teacher_email_domain.toLowerCase())) {
      console.log('Auto-assigning teacher role based on email domain');
      autoAssignUserTypeMutation.mutate({ userId: user.id, userType: 'teacher' });
      return;
    }
  }, [user, domainSettings, autoAssignUserTypeMutation]); // autoAssignUserTypeMutation added back to dependency array for correctness

  // Redirect users to their appropriate default page if they land on AdminDashboard without access
  useEffect(() => {
    if (!user || isLoading) return;

    const isMainAdmin = user?.role === 'admin';
    const isAP = user?.role === 'user' && user?.user_type === 'assistant_principal';
    const isPrincipal = user?.role === 'user' && user?.user_type === 'principal';
    const isCoach = user?.role === 'user' && user?.user_type === 'coach';
    const userType = user?.user_type || 'student';

    // Check if user is on AdminDashboard page
    const isOnAdminDashboard = location.pathname === createPageUrl("AdminDashboard") || 
                                location.pathname === '/' || 
                                location.pathname === '';

    if (isOnAdminDashboard && !isMainAdmin && !isAP && !isPrincipal) {
      // Coaches go straight to the ASD Attendance page — the only page they have.
      if (isCoach) {
        navigate(createPageUrl("ASDAttendance"), { replace: true });
      } else if (userType === 'ea_staff') {
        navigate(createPageUrl("EAStaffDashboard"), { replace: true });
      } else if (userType === 'driveline') {
        navigate(createPageUrl("DrivelineCheck"), { replace: true });
      } else if (userType === 'teacher') {
        navigate(createPageUrl("TeacherDashboard"), { replace: true });
      } else {
        // Default to student pages
        navigate(createPageUrl("StudentAnnouncements"), { replace: true });
      }
    }
  }, [user, isLoading, location.pathname, navigate]);

  const { data: contentItems = [] } = useQuery({
    queryKey: ['content'],
    queryFn: () => base44.entities.Content.list(),
    enabled: !!user,
  });

  const isMainAdmin = user?.role === 'admin';
  const userType = user?.user_type || 'student';
  const isAP = user?.role === 'user' && userType === 'assistant_principal';
  // Principal: same access as AP + sees ALL cohorts (no grade filter anywhere).
  const isPrincipal = user?.role === 'user' && userType === 'principal';
  // Coach: locked to the ASD Attendance page (Present-only).
  const isCoach = user?.role === 'user' && userType === 'coach';
  // Anywhere the sidebar/UI treats an AP as an "admin-side" viewer, a
  // Principal counts too. Coach never counts as admin-side.
  const isAPorPrincipal = isAP || isPrincipal;

  // Global system freeze: an Admin or AP can flip a Settings row
  // (setting_key === 'system_freeze') to `frozen: true`. When frozen, students
  // and teachers see a full-screen overlay banner that blocks every form,
  // click, and interaction under it until Admin/AP unfreezes.
  const freezeSetting = settings.find((s) => s.setting_key === 'system_freeze');
  const isSystemFrozen = freezeSetting?.frozen === true;
  const isStudentOrTeacher =
    !isMainAdmin && !isAPorPrincipal && !isCoach && (userType === 'student' || userType === 'teacher');
  const showFreezeOverlay = isSystemFrozen && isStudentOrTeacher;

  // Maintenance mode locks out every non-admin user with a full-screen
  // message. Persisted to Settings so it survives restarts.
  const maintenanceSetting = settings.find((s) => s.setting_key === 'maintenance_mode');
  const isMaintenanceOn = maintenanceSetting?.enabled === true;
  const showMaintenanceOverlay = isMaintenanceOn && !isMainAdmin;

  // NEW: Query for AP's students (if user is AP)
  const { data: myAPStudents = [] } = useQuery({
    queryKey: ['myAPStudentsCount', user?.email, user?.ap_grade_cohort],
    queryFn: async () => {
      if (!isAP || !user?.email || !user?.ap_grade_cohort) return [];
      const students = await base44.entities.EAMissTracking.filter({ 
        assigned_ap_email: user.email,
        status: 'active'
      });

      // Filter by cohort
      if (user.ap_grade_cohort === '4-5') {
        return students.filter(s => {
          const grade = parseInt(s.grade_level);
          return grade === 4 || grade === 5;
        });
      } else if (user.ap_grade_cohort === '6-8') {
        return students.filter(s => {
          const grade = parseInt(s.grade_level);
          return grade === 6 || grade === 7 || grade === 8;
        });
      }
      return [];
    },
    enabled: isAP && !!user?.email && !!user?.ap_grade_cohort,
  });

  const hasActiveAPCases = isAP && myAPStudents.length > 0;

  // NEW: Query for pending excusal submissions (admin only)
  const { data: allExcusals = [] } = useQuery({
    queryKey: ['allExcusals'],
    queryFn: () => base44.entities.ExcusalSubmission.list(),
    enabled: !!user && user?.role === 'admin',
  });

  const pendingExcusalsCount = isMainAdmin ? allExcusals.filter(e => e.status === 'pending').length : 0;

  const toggleMenu = (menuKey) => {
    setOpenMenus(prev => ({ ...prev, [menuKey]: !prev[menuKey] }));
  };

  const handleNavClick = () => {
    // Close mobile sidebar on navigation
    setOpenMobile(false);
  };

  // Check if there are new announcements (uses localStorage since we no longer persist last_viewed on the user record)
  const hasNewAnnouncements = useMemo(() => {
    if (!user || isMainAdmin) return false;
    const uid = user.id || user.user_id;
    if (!uid) return false;
    const contentKey = userType === 'teacher' ? 'teacher_announcements' : 'student_announcements';
    const announcement = contentItems.find(c => c.content_key === contentKey);
    if (!announcement?.last_updated) return false;
    const seenAt = localStorage.getItem(`eagle_seen_${contentKey}_${uid}`);
    if (!seenAt) return true;
    return new Date(announcement.last_updated) > new Date(seenAt);
  }, [user, contentItems, isMainAdmin, userType]);

  // EA Staff Navigation (Clerk & 2 EA Teachers) - READ ONLY
  const eaStaffNavItems = [
    {
      title: "EA Staff Dashboard",
      url: createPageUrl("EAStaffDashboard"),
      icon: LayoutDashboard,
    },
    {
      title: "Current EA Roster",
      url: createPageUrl("RunningRoster"),
      icon: ClipboardList,
    },
    {
      title: "Current ASD Roster",
      url: createPageUrl("ASDRoster"),
      icon: AlertTriangle,
    },
    {
      title: "EA Consequences",
      url: createPageUrl("ManageConsequences"),
      icon: AlertTriangle,
    },
    {
      title: "Driveline EA Check",
      url: createPageUrl("DrivelineCheck"),
      icon: Car,
    },
  ];

  // Driveline Navigation
  const drivelineNavItems = [
    {
      title: "Driveline EA Check",
      url: createPageUrl("DrivelineCheck"),
      icon: Car,
    },
  ];

  // Teacher Navigation
  const teacherNavItems = [
    {
      title: "Teacher Dashboard",
      url: createPageUrl("TeacherDashboard"),
      icon: LayoutDashboard,
    },
    {
      title: "Announcements",
      url: createPageUrl("TeacherAnnouncements"),
      icon: Megaphone,
      showBadge: hasNewAnnouncements,
    },
    {
      title: "My Roster",
      url: createPageUrl("TeacherRoster"),
      icon: ClipboardList,
    },
    {
      title: "Grade Level Roster",
      url: createPageUrl("GradeLevelRoster"),
      icon: GraduationCap,
    },
    {
      title: "Add Students to EA",
      url: createPageUrl("AddStudentsToEA"),
      icon: UserCheck,
    },
    {
      title: "Excusal Requests",
      url: createPageUrl("TeacherExcusals"),
      icon: FileText,
    },
    {
      title: "Driveline EA Check",
      url: createPageUrl("DrivelineCheck"),
      icon: Car,
    },
  ];

  // Coach Navigation — Coach can access the ASD Attendance page (Present-only)
  // and nothing else. No EA rosters, no consequences, no closeout, no reports.
  const coachNavItems = [
    {
      title: "ASD Attendance",
      url: createPageUrl("ASDAttendance"),
      icon: ClipboardList,
    },
  ];

  // Student Navigation
  const studentNavItems = [
    {
      title: "Announcements",
      url: createPageUrl("StudentAnnouncements"),
      icon: Megaphone,
      showBadge: hasNewAnnouncements,
    },
    {
      title: "My Assignments",
      url: createPageUrl("StudentDashboard"),
      icon: GraduationCap,
    },
    {
      title: "EA/ASD Guidelines",
      url: createPageUrl("StudentGuidelines"),
      icon: FileText,
    },
  ];

  // Determine which navigation to use
  let navigationItems = [];
  let userTypeLabel = "";
  
  if (!isLoading && user) {
    if (isMainAdmin) {
      userTypeLabel = "Administrator";
    } else if (isPrincipal) {
      userTypeLabel = "Principal";
    } else if (isAP) {
      userTypeLabel = "Assistant Principal";
    } else if (user?.role === 'user') {
      if (userType === 'ea_staff') {
        navigationItems = eaStaffNavItems;
        userTypeLabel = "EA Staff";
      } else if (userType === 'driveline') {
        navigationItems = drivelineNavItems;
        userTypeLabel = "Driveline Staff";
      } else if (userType === 'coach') {
        navigationItems = coachNavItems;
        userTypeLabel = "Coach";
      } else if (userType === 'teacher') {
        navigationItems = teacherNavItems;
        userTypeLabel = "Teacher";
      } else {
        navigationItems = studentNavItems;
        userTypeLabel = "Student";
      }
    }
  }

  const getUserTypeDisplay = () => {
    if (isMainAdmin) return 'Administrator';
    if (isPrincipal) return 'Principal';
    if (isAP) return 'Assistant Principal';
    if (user?.role === 'user') {
      if (userType === 'ea_staff') return 'EA Staff';
      if (userType === 'driveline') return 'Driveline Staff';
      if (userType === 'coach') return 'Coach';
      if (userType === 'teacher') return 'Teacher';
      return 'Student';
    }
    return 'Guest';
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Deny access if user doesn't have authorized email domain
  if (user && !isAuthorizedDomain) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-xl shadow-xl p-8 border-2 border-red-200">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-4">
              Access Denied
            </h1>
            <div className="space-y-4 text-slate-700">
              <p className="text-center">
                This application is restricted to <strong>ILTexas staff and students only</strong>.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2 text-sm">
                <p><strong>Your email:</strong> {user.email}</p>
                <p className="text-red-800">
                  <strong>⚠️ Only authorized domains can access this system:</strong>
                </p>
                <ul className="list-disc list-inside ml-2 text-red-800">
                  <li>@iltexasstudent.org (Students)</li>
                  <li>@iltexas.org (Staff)</li>
                </ul>
              </div>
              <p className="text-sm text-slate-600 text-center">
                If you believe this is an error, please contact your campus administrator.
              </p>
            </div>
            <div className="mt-6 flex justify-center">
              <button
                onClick={() => base44.auth.logout()}
                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 to-blue-50">
      <Sidebar className="border-r border-slate-200">
        <SidebarHeader className="border-b border-slate-200 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg overflow-hidden bg-white">
              <img 
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68fd862fecaf3cb27ae4c954/a5a775673_71d4283c3_logo.png" 
                alt="Eagle Academy Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h2 className="font-bold text-slate-900 text-lg">Eagle Academy</h2>
              <p className="text-xs text-slate-500">Management System</p>
            </div>
          </div>
        </SidebarHeader>
        
        <SidebarContent className="p-3">
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
              {userTypeLabel}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {isMainAdmin || isAPorPrincipal ? (
                  <>
                    {/* Admin Dashboard */}
                    <SidebarMenuItem>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === createPageUrl("AdminDashboard") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                        }`}
                      >
                        <Link to={createPageUrl("AdminDashboard")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                          <LayoutDashboard className="w-4 h-4" />
                          <span>Admin Dashboard</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* Excusal Requests - Admin only */}
                    {isMainAdmin && (
                      <>
                        <SidebarMenuItem>
                          <SidebarMenuButton 
                            asChild 
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("TeacherExcusals") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("TeacherExcusals")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5 relative">
                              <FileText className="w-4 h-4" />
                              <span>Excusal Requests</span>
                              {pendingExcusalsCount > 0 && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                                  {pendingExcusalsCount}
                                </span>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                        {/* Sub-item: Excusal Summary — indented under Excusal Requests. */}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("ExcusalSummary") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link
                              to={createPageUrl("ExcusalSummary")}
                              onClick={handleNavClick}
                              className="flex items-center gap-3 pl-9 pr-3 py-2 text-sm"
                              data-testid="sidebar-excusal-summary-link"
                            >
                              <span className="w-1 h-1 bg-slate-400 rounded-full" aria-hidden="true"></span>
                              <span>Summary</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      </>
                    )}

                    {/* Courtesy Excusals - Admin & AP & Principal */}
                    {(isMainAdmin || isAPorPrincipal) && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                            location.pathname === createPageUrl("CourtesyExcusals") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                          }`}
                        >
                          <Link
                            to={createPageUrl("CourtesyExcusals")}
                            onClick={handleNavClick}
                            className="flex items-center gap-3 px-3 py-2.5"
                            data-testid="sidebar-courtesy-excusals-link"
                          >
                            <ShieldCheck className="w-4 h-4" />
                            <span>Courtesy Excusals</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {/* EA Consequences - Top level for APs (cohort filtered) and Principals (all grades, admin view) */}
                    {isAP && (
                      <SidebarMenuItem>
                        <SidebarMenuButton 
                          asChild 
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                            location.pathname === createPageUrl("APConsequences") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                          }`}
                        >
                          <Link to={createPageUrl("APConsequences")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5 relative">
                            <AlertTriangle className="w-4 h-4" />
                            <span>EA Consequences</span>
                            {hasActiveAPCases && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                    {isPrincipal && (
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                            location.pathname === createPageUrl("ManageConsequences") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                          }`}
                        >
                          <Link to={createPageUrl("ManageConsequences")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5" data-testid="principal-consequences-link">
                            <AlertTriangle className="w-4 h-4" />
                            <span>EA Consequences</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}

                    {/* EA System Submenu */}
                    <Collapsible open={openMenus.eaSystem} onOpenChange={() => toggleMenu('eaSystem')} className="group/collapsible">
                      <SidebarMenuItem>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton className="hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1">
                            <FolderTree className="w-4 h-4" />
                            <span>EA System</span>
                            {openMenus.eaSystem ? (
                              <ChevronDown className="ml-auto w-4 h-4 transition-transform" />
                            ) : (
                              <ChevronRight className="ml-auto w-4 h-4 transition-transform" />
                            )}
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton 
                                asChild
                                className={`hover:bg-blue-50 hover:text-blue-700 ${
                                  location.pathname === createPageUrl("RunningRoster") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                }`}
                              >
                                <Link to={createPageUrl("RunningRoster")} onClick={handleNavClick}>
                                  <span>EA Roster</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton 
                                asChild
                                className={`hover:bg-blue-50 hover:text-blue-700 ${
                                  location.pathname === createPageUrl("Attendance") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                }`}
                              >
                                <Link to={createPageUrl("Attendance")} onClick={handleNavClick}>
                                  <span>EA Attendance</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton 
                                asChild
                                className={`hover:bg-blue-50 hover:text-blue-700 ${
                                  location.pathname === createPageUrl("ASDRoster") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                }`}
                              >
                                <Link to={createPageUrl("ASDRoster")} onClick={handleNavClick}>
                                  <span>ASD Roster</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            <SidebarMenuSubItem>
                              <SidebarMenuSubButton 
                                asChild
                                className={`hover:bg-blue-50 hover:text-blue-700 ${
                                  location.pathname === createPageUrl("ASDAttendance") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                }`}
                              >
                                <Link to={createPageUrl("ASDAttendance")} onClick={handleNavClick}>
                                  <span>ASD Attendance</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                            {/* EA Consequences only in submenu for Admins */}
                            {isMainAdmin && (
                              <SidebarMenuSubItem>
                                <SidebarMenuSubButton 
                                  asChild
                                  className={`hover:bg-blue-50 hover:text-blue-700 ${
                                    location.pathname === createPageUrl("ManageConsequences") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                  }`}
                                >
                                  <Link to={createPageUrl("ManageConsequences")} onClick={handleNavClick}>
                                    <span>EA Consequences</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            )}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>

                    {/* Reports & Summary */}
                    <SidebarMenuItem>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === createPageUrl("Reports") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                        }`}
                      >
                        <Link to={createPageUrl("Reports")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                          <FileText className="w-4 h-4" />
                          <span>Reports & Summary</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {/* Attendance Logs — Admin, AP, Principal */}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === createPageUrl("AttendanceLogs") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                        }`}
                      >
                        <Link
                          to={createPageUrl("AttendanceLogs")}
                          onClick={handleNavClick}
                          className="flex items-center gap-3 px-3 py-2.5"
                          data-testid="sidebar-attendance-logs-link"
                        >
                          <ClipboardList className="w-4 h-4" />
                          <span>Attendance Logs</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {isMainAdmin && (
                      <>
                        {/* User Types Submenu */}
                        <Collapsible open={openMenus.userTypes} onOpenChange={() => toggleMenu('userTypes')} className="group/collapsible">
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton className="hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1">
                                <Users className="w-4 h-4" />
                                <span>User Types</span>
                                {openMenus.userTypes ? (
                                  <ChevronDown className="ml-auto w-4 h-4 transition-transform" />
                                ) : (
                                  <ChevronRight className="ml-auto w-4 h-4 transition-transform" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("Students") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("Students")} onClick={handleNavClick}>
                                      <span>Students</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("Teachers") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("Teachers")} onClick={handleNavClick}>
                                      <span>Teachers</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("EAStaff") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("EAStaff")} onClick={handleNavClick}>
                                      <span>EA Staff</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("DrivelineStaff") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("DrivelineStaff")} onClick={handleNavClick}>
                                      <span>Driveline Staff</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("Users") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("Users")} onClick={handleNavClick}>
                                      <span>Users</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>

                        {/* Manage Content */}
                        <SidebarMenuItem>
                          <SidebarMenuButton 
                            asChild 
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("ManageContent") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("ManageContent")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                              <FileText className="w-4 h-4" />
                              <span>Manage Content</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Data - manage all entity tables */}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("DataAdmin") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("DataAdmin")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                              <FolderTree className="w-4 h-4" />
                              <span>Data</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Upload Student Roster */}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("UploadStudents") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("UploadStudents")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                              <Upload className="w-4 h-4" />
                              <span>Upload Roster</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Staff Manager */}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("StaffManager") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("StaffManager")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                              <UserCog className="w-4 h-4" />
                              <span>Manage Staff</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Integrations Health */}
                        <SidebarMenuItem>
                          <SidebarMenuButton
                            asChild
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("IntegrationsHealth") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("IntegrationsHealth")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                              <Activity className="w-4 h-4" />
                              <span>Health Check</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Support Tickets - NEW */}
                        <SidebarMenuItem>
                          <SidebarMenuButton 
                            asChild 
                            className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                              location.pathname === createPageUrl("ManageTickets") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                            }`}
                          >
                            <Link to={createPageUrl("ManageTickets")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5 relative">
                              <MessageSquare className="w-4 h-4" />
                              <span>Support Tickets</span>
                              {newTicketsCount > 0 && (
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                                  {newTicketsCount}
                                </span>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>

                        {/* Settings Submenu with Import Data */}
                        <Collapsible open={openMenus.settings} onOpenChange={() => toggleMenu('settings')} className="group/collapsible">
                          <SidebarMenuItem>
                            <CollapsibleTrigger asChild>
                              <SidebarMenuButton className="hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1">
                                <LayoutDashboard className="w-4 h-4" />
                                <span>Settings</span>
                                {openMenus.settings ? (
                                  <ChevronDown className="ml-auto w-4 h-4 transition-transform" />
                                ) : (
                                  <ChevronRight className="ml-auto w-4 h-4 transition-transform" />
                                )}
                              </SidebarMenuButton>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <SidebarMenuSub>
                                <SidebarMenuSubButton 
                                  asChild
                                  className={`hover:bg-blue-50 hover:text-blue-700 ${
                                    location.pathname === createPageUrl("Settings") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                  }`}
                                >
                                  <Link to={createPageUrl("Settings")} onClick={handleNavClick}>
                                    <span>System Settings</span>
                                  </Link>
                                </SidebarMenuSubButton>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("ConsequenceSettings") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("ConsequenceSettings")} onClick={handleNavClick}>
                                      <span>Consequence Levels</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("EmailTemplateSettings") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("EmailTemplateSettings")} onClick={handleNavClick}>
                                      <span>Email Templates</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("ImportHistoricalData") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("ImportHistoricalData")} onClick={handleNavClick}>
                                      <span>Import Data</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                                <SidebarMenuSubItem>
                                  <SidebarMenuSubButton 
                                    asChild
                                    className={`hover:bg-blue-50 hover:text-blue-700 ${
                                      location.pathname === createPageUrl("RestoreData") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                                    }`}
                                  >
                                    <Link to={createPageUrl("RestoreData")} onClick={handleNavClick}>
                                      <span>Restore Data</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </SidebarMenuSubItem>
                              </SidebarMenuSub>
                            </CollapsibleContent>
                          </SidebarMenuItem>
                        </Collapsible>
                      </>
                    )}

                    {/* Driveline EA Check */}
                    <SidebarMenuItem>
                      <SidebarMenuButton 
                        asChild 
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === createPageUrl("DrivelineCheck") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                        }`}
                      >
                        <Link to={createPageUrl("DrivelineCheck")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                          <Car className="w-4 h-4" />
                          <span>Driveline EA Check</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </>
                ) : (
                  <>
                    {navigationItems.map((item) => (
                      <SidebarMenuItem key={item.title}>
                        <SidebarMenuButton 
                          asChild 
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                            location.pathname === item.url ? 'bg-blue-100 text-blue-700 font-medium' : ''
                          }`}
                        >
                          <Link to={item.url} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5 relative">
                            <item.icon className="w-4 h-4" />
                            <span>{item.title}</span>
                            {item.showBadge && (
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 w-2 h-2 bg-red-600 rounded-full animate-pulse"></span>
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}

                    {/* Submit Ticket - NEW - For non-students */}
                    {userType !== 'student' && (
                      <SidebarMenuItem>
                        <SidebarMenuButton 
                          asChild 
                          className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                            location.pathname === createPageUrl("SubmitTicket") ? 'bg-blue-100 text-blue-700 font-medium' : ''
                          }`}
                        >
                          <Link to={createPageUrl("SubmitTicket")} onClick={handleNavClick} className="flex items-center gap-3 px-3 py-2.5">
                            <MessageSquare className="w-4 h-4" />
                            <span>Submit Ticket</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    )}
                  </>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="border-t border-slate-200 p-4">
          <RoleSwitcher />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-br from-slate-200 to-slate-300 rounded-full flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-slate-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-900 text-sm truncate">{user?.full_name || "Guest"}</p>
                <p className="text-xs text-slate-500 truncate">{getUserTypeDisplay()}</p>
              </div>
            </div>
            <button
              onClick={() => base44.auth.logout()}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Logout"
            >
              <LogOut className="w-4 h-4 text-slate-500" />
            </button>
          </div>
        </SidebarFooter>
      </Sidebar>

      <main className="flex-1 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center gap-4">
            <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200" />
            <h1 className="text-xl font-bold text-slate-900 md:hidden">Eagle Academy</h1>
          </div>
        </header>

        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </main>

      {showMaintenanceOverlay && (
        <div
          data-testid="maintenance-overlay"
          className="fixed inset-0 z-[9998] bg-slate-900 flex items-center justify-center p-6"
          onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onKeyDownCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onSubmitCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
        >
          <div className="max-w-2xl w-full bg-white rounded-2xl shadow-2xl p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-12 h-12 text-slate-700" />
              </div>
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-3">
              This app is currently unavailable.
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Please check back later.
            </p>
          </div>
        </div>
      )}

      {showFreezeOverlay && (
        <div
          data-testid="system-freeze-overlay"
          className="fixed inset-0 z-[9999] bg-slate-900/95 backdrop-blur-sm flex items-center justify-center p-6"
          // Capture every event so no click, form submit, or keystroke reaches
          // the underlying page while the system is frozen.
          onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onKeyDownCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
          onSubmitCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
        >
          <div className="max-w-2xl w-full bg-gradient-to-br from-red-800 to-red-900 text-white rounded-2xl shadow-2xl border-4 border-red-950 p-10 text-center">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-white/15 rounded-full flex items-center justify-center animate-pulse">
                <AlertTriangle className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-3xl font-extrabold tracking-tight mb-4 uppercase">
              Closeout in Process
            </h2>
            <p className="text-lg text-white/90 leading-relaxed">
              Eagle Academy is updating. Please check back momentarily.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  return (
    <SidebarProvider>
      <LayoutContent>{children}</LayoutContent>
    </SidebarProvider>
  );
}